import styled from 'styled-components';

import { Environment } from 'dashboard/models/environment';
import { RolePermission } from 'dashboard/models/role';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import { Permission } from 'lib/permissions';

export interface EnvironmentsTableProps {
  disabled: boolean;
  environments: Environment[];
  isAdmin: boolean;
  rolePermissions: RolePermission[];
  allCurrentAndFutureEnvironmentSelected: boolean;
  selectedEnvironmentIds: string[];
  setRolePermissions: (permissions: RolePermission[]) => void;
}

const EnvironmentsTable = ({
  disabled,
  environments,
  isAdmin,
  rolePermissions,
  allCurrentAndFutureEnvironmentSelected,
  selectedEnvironmentIds,
  setRolePermissions,
}: EnvironmentsTableProps) => {
  const isSelected = (environmentId: string) => {
    return rolePermissions.some(
      (rp) => rp.name === Permission.EnvironmentView && rp['environment-id'] === environmentId,
    );
  };

  const handleSelectEnvironmentId = (environmentId: string) => {
    if (isSelected(environmentId)) {
      setRolePermissions(rolePermissions.filter((rp) => rp['environment-id'] !== environmentId));
    } else {
      const anyExistingEnvironmentId = rolePermissions.find((rp) => rp['environment-id'] != null)?.[
        'environment-id'
      ];

      const newEnvironmenViewRolePermission = {
        name: Permission.EnvironmentView,
        'environment-id': environmentId,
      };

      if (anyExistingEnvironmentId != null) {
        const newRolePermissions: RolePermission[] = rolePermissions
          .filter(
            (rp) =>
              rp['environment-id'] === anyExistingEnvironmentId &&
              rp.name !== Permission.EnvironmentView,
          )
          .map((rp) => {
            return { ...rp, 'environment-id': environmentId };
          });

        setRolePermissions([
          ...rolePermissions,
          ...newRolePermissions,
          newEnvironmenViewRolePermission,
        ]);
      } else {
        setRolePermissions([...rolePermissions, newEnvironmenViewRolePermission]);
      }
    }
  };

  return (
    <Table>
      <TableHead>
        <StyledTableRow>
          <StyledTableHeaderCell>
            <Paragraph color='primary'>Environment</Paragraph>
          </StyledTableHeaderCell>
        </StyledTableRow>
      </TableHead>
      <TableBody>
        {environments.map((environment) => {
          return (
            <StyledTableRow
              id={`roles-show-environments-table__row-${environment.id}`}
              key={environment.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectedEnvironmentIds.length > 0 && handleSelectEnvironmentId(environment.id);
              }}
            >
              <StyledCheckboxCell>
                <Checkbox
                  id={`roles-show-environments-table__environment-checkbox--${environment.id}`}
                  checked={
                    isAdmin || isSelected(environment.id) || allCurrentAndFutureEnvironmentSelected
                  }
                  disabled={disabled || allCurrentAndFutureEnvironmentSelected}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectEnvironmentId(environment.id);
                  }}
                />
                <Spacer width={1} />
                <Paragraph color='primary'>{environment.attributes.name}</Paragraph>
              </StyledCheckboxCell>
            </StyledTableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default EnvironmentsTable;

const StyledCheckboxCell = styled(TableCell)`
  height: 44px;
  display: flex;
`;

const StyledTableHeaderCell = styled(TableHeaderCell)`
  padding: 0 12px 0 12px;
`;

const StyledTableRow = styled(TableRow)`
  height: 44px;
`;
